import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Home, ShoppingCart } from 'lucide-react';
import mapImage from '../assets/contact/map-image.jpg';

const ContactFormSection: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    question: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));

    setIsSubmitting(false);
    setSubmitStatus('success');

    setTimeout(() => {
      setSubmitStatus('idle');
      setFormData({ name: '', email: '', question: '' });
    }, 3000);
  };

  return (
    <section className="bg-[#ffecc6] py-16 lg:py-20">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" style={{ paddingLeft: 'clamp(16px, 8.89vw, 132px)', paddingRight: 'clamp(16px, 8.89vw, 132px)' }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-[20px] shadow-[0px_0px_2px_0px_rgba(23,26,31,0.12),0px_4px_9px_0px_rgba(23,26,31,0.11)] overflow-hidden"
          style={{ minHeight: '576px' }}
        >
          <div className="grid lg:grid-cols-2 gap-0 min-h-[576px]">
            {/* Left Side - Contact Form */}
            <div className="p-8 sm:p-12 lg:p-[100px]">
              <h2
                className="font-opensans text-[36px] sm:text-[42px] lg:text-[52px] leading-[48px] sm:leading-[60px] lg:leading-[74px] text-text-primary font-bold mb-8 lg:mb-12"
                style={{ fontVariationSettings: "'wdth' 100" }}
              >
                Contact us
              </h2>

              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-green-100 border border-green-200 rounded-xl"
                >
                  <p className="text-green-800 font-medium text-sm">
                    Thank you! We'll get back to you soon.
                  </p>
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Field */}
                <div>
                  <label htmlFor="name" className="block font-manrope text-[12px] leading-[20px] text-text-secondary mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your name"
                    className="w-full px-4 py-3 border border-[#bdc1ca] rounded-[8px] focus:ring-2 focus:ring-brand-blue focus:border-transparent transition-all bg-white font-manrope text-[16px] leading-[26px] text-text-primary placeholder:text-[#bdc1ca]"
                    style={{ maxWidth: '420px' }}
                  />
                </div>

                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block font-manrope text-[12px] leading-[20px] text-text-secondary mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 border border-[#bdc1ca] rounded-[8px] focus:ring-2 focus:ring-brand-blue focus:border-transparent transition-all bg-white font-manrope text-[16px] leading-[26px] text-text-primary placeholder:text-[#bdc1ca]"
                    style={{ maxWidth: '420px' }}
                  />
                </div>

                {/* Question Field */}
                <div>
                  <label htmlFor="question" className="block font-manrope text-[12px] leading-[20px] text-text-secondary mb-2">
                    Question
                  </label>
                  <textarea
                    id="question"
                    name="question"
                    value={formData.question}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    placeholder="Enter question or feedback"
                    className="w-full px-4 py-3 border border-[#bdc1ca] rounded-[8px] focus:ring-2 focus:ring-brand-blue focus:border-transparent transition-all bg-white font-manrope text-[16px] leading-[26px] text-text-primary placeholder:text-[#bdc1ca] resize-none"
                    style={{ maxWidth: '420px' }}
                  />
                </div>

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-brand-yellow text-[#704b00] font-manrope font-normal text-[16px] leading-[26px] px-6 py-2 rounded-[8px] hover:bg-brand-yellow/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ width: '210px', height: '44px' }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </motion.button>
              </form>
            </div>

            {/* Right Side - Map Image */}
            <div className="relative w-full lg:block hidden" style={{ maxWidth: '557px', minHeight: '400px', height: '576px' }}>
              <div className="absolute inset-0 overflow-hidden">
                <img
                  src={mapImage}
                  alt="Location map"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Map Controls and Icons */}
              <div className="absolute inset-0 pointer-events-none">
                {/* Location Pin (Yellow) */}
                <div className="absolute left-[349px] top-[167px]">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.8, type: 'spring' }}
                    className="bg-brand-yellow rounded-[22px] shadow-[0px_0px_2px_0px_rgba(255,200,87,0.12),0px_17px_35px_0px_rgba(255,200,87,0.24)] w-11 h-11 flex items-center justify-center"
                  >
                    <MapPin className="w-5 h-5 text-[#704b00]" />
                  </motion.div>
                </div>

                {/* Shopping Cart Icons */}
                <div className="absolute left-[163px] top-[113px]">
                  <div className="bg-white border border-[#9095a1] rounded-[18px] shadow-[0px_0px_2px_0px_rgba(23,26,31,0.12),0px_2px_5px_0px_rgba(23,26,31,0.09)] w-9 h-9 flex items-center justify-center">
                    <ShoppingCart className="w-4 h-4 text-[#9095a1]" />
                  </div>
                </div>

                <div className="absolute left-[260px] top-[113px]">
                  <div className="bg-white border border-[#9095a1] rounded-[18px] shadow-[0px_0px_2px_0px_rgba(23,26,31,0.12),0px_2px_5px_0px_rgba(23,26,31,0.09)] w-9 h-9 flex items-center justify-center">
                    <ShoppingCart className="w-4 h-4 text-[#9095a1]" />
                  </div>
                </div>

                {/* Home Icon */}
                <div className="absolute left-[268px] top-[218px]">
                  <div className="bg-white border border-[#9095a1] rounded-[18px] shadow-[0px_0px_2px_0px_rgba(23,26,31,0.12),0px_2px_5px_0px_rgba(23,26,31,0.09)] w-9 h-9 flex items-center justify-center">
                    <Home className="w-4 h-4 text-[#9095a1]" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactFormSection;
